import React, { createContext, useContext, useState, useMemo } from 'react';
import { useAuthStatus } from '@/hooks/useApi';

export type RoleView = 'preparer' | 'approver' | 'full' | 'admin' | 'auditor';

interface RoleViewContextType {
  activeView: RoleView;
  setActiveView: (view: RoleView) => void;
  availableViews: RoleView[];
  viewCounts: Record<RoleView, number>;
  updateViewCount: (view: RoleView, count: number) => void;
  /** The user's roles are known, so activeView won't change by itself any more (lists wait for it: one fetch). */
  ready?: boolean;
}

const RoleViewContext = createContext<RoleViewContextType | undefined>(undefined);

export const useRoleView = () => {
  const context = useContext(RoleViewContext);
  if (!context) {
    throw new Error('useRoleView must be used within a RoleViewProvider');
  }
  return context;
};

/** The views a user's roles offer. C94: the auditor view (read-only, no Start buttons) only when auditor is the
 *  user's highest role: someone who can also prepare, approve or administer gets their working views instead. */
const WORKING_ROLES = ['preparer', 'approver', 'senior_approver', 'admin'];

export function viewsFor(roles: string[] | undefined): RoleView[] {
  const views: RoleView[] = [];
  if (!roles) return views;
  if (roles.includes('preparer')) views.push('preparer');
  if (roles.includes('approver')) views.push('approver');
  if (roles.includes('admin')) {
    views.push('admin');
    views.push('full'); // Admins always get full view
  }
  if (roles.includes('auditor') && !roles.some(r => WORKING_ROLES.includes(r))) views.push('auditor');
  // If user has multiple roles, add full view option
  if (views.length > 1 && !views.includes('full')) views.push('full');
  // Senior approvers get all views
  if (roles.includes('senior_approver')) {
    if (!views.includes('preparer')) views.push('preparer');
    if (!views.includes('approver')) views.push('approver');
    if (!views.includes('full')) views.push('full');
  }
  return views;
}

/** The view in force: the remembered one if the user's roles still offer it (a saved 'auditor' from another account
 *  or an old role no longer applies); else the auditor view for an auditor-only user, and the full view (the default
 *  as before) for everyone else. */
export function effectiveView(saved: RoleView | null, views: RoleView[]): RoleView {
  if (saved && views.includes(saved)) return saved;
  if (views.length === 1 && views[0] === 'auditor') return 'auditor';
  return 'full';
}

export const RoleViewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: authStatus } = useAuthStatus();
  const [activeView, setActiveViewState] = useState<RoleView>(() => {
    // Try to get saved preference first
    const savedView = localStorage.getItem('preferredRoleView') as RoleView;
    return savedView || 'full';
  });
  const [viewCounts, setViewCounts] = useState<Record<RoleView, number>>({
    preparer: 0,
    approver: 0,
    full: 0,
    admin: 0,
    auditor: 0,
  });

  // Determine available views based on user roles
  const availableViews = useMemo(() => viewsFor(authStatus?.user?.roles), [authStatus?.user?.roles]);

  // Remove the problematic useEffect to fix infinite loop issue
  // Will handle view initialization differently if needed

  const setActiveView = (view: RoleView) => {
    if (availableViews.includes(view)) {
      setActiveViewState(view);
      localStorage.setItem('preferredRoleView', view);
    }
  };

  const updateViewCount = React.useCallback((view: RoleView, count: number) => {
    setViewCounts(prev => ({
      ...prev,
      [view]: count
    }));
  }, []);

  return (
    <RoleViewContext.Provider
      value={{
        activeView: authStatus?.user ? effectiveView(activeView, availableViews) : activeView,
        setActiveView,
        availableViews,
        viewCounts,
        updateViewCount,
        ready: !!authStatus?.user,
      }}
    >
      {children}
    </RoleViewContext.Provider>
  );
};